import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ShieldCheck, UserPlus, Save, RefreshCw, KeyRound, Users } from 'lucide-react';
import { toast } from 'react-toastify';
import { SALON_API_URL } from '../../api/config';
import { SALON_ROLE_DEFINITIONS } from '../../config/salonPermissions';
import { useAuth } from '../../common/context/AuthContext';

const initialForm = {
  id: null,
  fullName: '',
  email: '',
  role: 'manager',
  staffId: '',
  password: '',
  isActive: true,
};

export default function SalonUserAccessSection() {
  const { userToken: token, hasSalonPermission } = useAuth();
  const canView = hasSalonPermission('users.view');
  const canManage = hasSalonPermission('users.manage');

  const [users, setUsers] = useState([]);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(initialForm);

  const editableRoles = useMemo(() => Object.entries(SALON_ROLE_DEFINITIONS).filter(([key]) => key !== 'owner'), []);

  const load = useCallback(async () => {
    if (!token || !canView) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const [usersRes, staffRes] = await Promise.all([
        fetch(SALON_API_URL + '/salon-users', { headers: { Authorization: 'Bearer ' + token } }),
        fetch(SALON_API_URL + '/staff', { headers: { Authorization: 'Bearer ' + token } }),
      ]);
      const [usersData, staffData] = await Promise.all([usersRes.json(), staffRes.json()]);
      if (usersData.success) setUsers(usersData.data || []);
      if (staffData.success) setStaff(staffData.data || []);
    } catch {
      toast.error('Failed to load salon users');
    } finally {
      setLoading(false);
    }
  }, [token, canView]);

  useEffect(() => { load(); }, [load]);

  const startEdit = (user) => {
    setForm({
      id: user.id,
      fullName: user.full_name || '',
      email: user.email || '',
      role: user.role || 'manager',
      staffId: user.staff_id || '',
      password: '',
      isActive: !!user.is_active,
    });
  };

  const resetForm = () => setForm(initialForm);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canManage) return;
    if (!form.fullName.trim() || !form.email.trim() || !form.role) {
      toast.error('Name, email, and role are required');
      return;
    }
    if (!form.id && form.password.trim().length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    if (form.password && form.password.trim().length > 0 && form.password.trim().length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        fullName: form.fullName.trim(),
        email: form.email.trim().toLowerCase(),
        role: form.role,
        staffId: form.staffId || null,
        isActive: form.isActive,
      };
      if (form.password.trim()) payload.password = form.password.trim();

      const url = SALON_API_URL + '/salon-users' + (form.id ? '/' + form.id : '');
      const method = form.id ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!data.success) {
        toast.error(data.message || 'Failed to save salon user');
        return;
      }
      toast.success(form.id ? 'Salon user updated' : 'Salon user created');
      resetForm();
      load();
    } catch {
      toast.error('Network error while saving salon user');
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async (userId) => {
    if (!canManage) return;
    try {
      const res = await fetch(SALON_API_URL + '/salon-users/' + userId, {
        method: 'DELETE',
        headers: { Authorization: 'Bearer ' + token },
      });
      const data = await res.json();
      if (!data.success) {
        toast.error(data.message || 'Failed to deactivate salon user');
        return;
      }
      toast.success('Salon user deactivated');
      load();
    } catch {
      toast.error('Network error while deactivating salon user');
    }
  };

  if (!canView) return null;

  return (
    <div className="bg-white dark:bg-slate-800 rounded-3xl border border-gray-100 dark:border-slate-700 overflow-hidden">
      <div className="px-6 pt-6 pb-4 border-b border-gray-50 dark:border-slate-700">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-gray-50 dark:bg-slate-700 flex items-center justify-center">
            <ShieldCheck size={18} className="text-purple-500" />
          </div>
          <div>
            <h2 className="font-black text-gray-900 dark:text-white text-base">Team Access</h2>
            <p className="text-xs text-gray-400 mt-0.5">Create separate salon logins with role-based access</p>
          </div>
        </div>
      </div>

      <div className="p-6 space-y-8">
        {/* Form Container (On Top) */}
        {canManage ? (
          <form onSubmit={handleSubmit} className="rounded-2xl border border-gray-150 dark:border-slate-700 bg-gray-50/70 dark:bg-slate-900/40 p-5 space-y-5">
            <div className="flex items-center gap-2 pb-2 border-b border-gray-200/50 dark:border-slate-700/50">
              {form.id ? <Users size={16} className="text-purple-500" /> : <UserPlus size={16} className="text-purple-500" />}
              <h3 className="text-sm font-black text-gray-900 dark:text-white">{form.id ? 'Edit User Account' : 'Add New User'}</h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <label className="text-xs font-bold text-gray-400 uppercase tracking-widest block mb-1.5">Full name</label>
                <input
                  value={form.fullName}
                  onChange={(e) => setForm((prev) => ({ ...prev, fullName: e.target.value }))}
                  className="w-full px-4 py-3 bg-white dark:bg-slate-800 border border-gray-205 dark:border-slate-700 rounded-2xl text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 text-gray-800 dark:text-white transition-all"
                  placeholder="e.g. Front Desk"
                />
              </div>

              <div>
                <label className="text-xs font-bold text-gray-400 uppercase tracking-widest block mb-1.5">Email</label>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm((prev) => ({ ...prev, email: e.target.value }))}
                  className="w-full px-4 py-3 bg-white dark:bg-slate-800 border border-gray-205 dark:border-slate-700 rounded-2xl text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 text-gray-800 dark:text-white transition-all"
                  placeholder="team@salon.com"
                />
              </div>

              <div>
                <label className="text-xs font-bold text-gray-400 uppercase tracking-widest block mb-1.5">Role</label>
                <select
                  value={form.role}
                  onChange={(e) => setForm((prev) => ({ ...prev, role: e.target.value }))}
                  className="w-full px-4 py-3 bg-white dark:bg-slate-800 border border-gray-205 dark:border-slate-700 rounded-2xl text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 text-gray-800 dark:text-white cursor-pointer transition-all"
                >
                  {editableRoles.map(([key, meta]) => <option key={key} value={key}>{meta.label}</option>)}
                </select>
              </div>

              <div>
                <label className="text-xs font-bold text-gray-400 uppercase tracking-widest block mb-1.5">Linked staff (optional)</label>
                <select
                  value={form.staffId}
                  onChange={(e) => setForm((prev) => ({ ...prev, staffId: e.target.value }))}
                  className="w-full px-4 py-3 bg-white dark:bg-slate-800 border border-gray-205 dark:border-slate-700 rounded-2xl text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 text-gray-800 dark:text-white cursor-pointer transition-all"
                >
                  <option value="">No linked staff</option>
                  {staff.map((member) => <option key={member.id} value={member.id}>{member.name}</option>)}
                </select>
              </div>

              <div>
                <label className="text-xs font-bold text-gray-400 uppercase tracking-widest block mb-1.5">
                  {form.id ? 'Reset password (optional)' : 'Password'}
                </label>
                <div className="relative">
                  <KeyRound size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="password"
                    value={form.password}
                    onChange={(e) => setForm((prev) => ({ ...prev, password: e.target.value }))}
                    className="w-full pl-11 pr-4 py-3 bg-white dark:bg-slate-800 border border-gray-205 dark:border-slate-700 rounded-2xl text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 text-gray-800 dark:text-white transition-all"
                    placeholder={form.id ? 'Leave blank to keep current password' : 'Minimum 6 characters'}
                  />
                </div>
              </div>

              {form.id ? (
                <div className="flex items-end pb-3">
                  <label className="flex items-center gap-2 text-sm font-bold text-gray-650 dark:text-slate-350 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.isActive}
                      onChange={(e) => setForm((prev) => ({ ...prev, isActive: e.target.checked }))}
                      className="w-4 h-4 rounded text-purple-605 accent-purple-605 cursor-pointer"
                    />
                    Active user
                  </label>
                </div>
              ) : null}
            </div>

            <div className="flex gap-3 pt-1 max-w-xs">
              <button
                type="submit"
                disabled={saving}
                className="inline-flex items-center justify-center gap-2 flex-1 px-5 py-3 rounded-2xl bg-purple-600 hover:bg-purple-750 disabled:opacity-60 text-white text-sm font-black transition-all active:scale-98"
              >
                <Save size={16} /> {saving ? 'Saving...' : (form.id ? 'Update User' : 'Create User')}
              </button>
              <button
                type="button"
                onClick={resetForm}
                className="px-5 py-3 rounded-2xl border border-gray-250 dark:border-slate-700 text-sm font-bold text-gray-650 dark:text-slate-200 hover:bg-gray-100 dark:hover:bg-slate-800 transition-all"
              >
                Clear
              </button>
            </div>
          </form>
        ) : null}

        {/* Existing Users Container (Below Form) */}
        <div className="space-y-4 pt-1">
          <div className="flex items-center justify-between border-b border-gray-50 dark:border-slate-800/80 pb-2">
            <h3 className="text-sm font-black text-gray-900 dark:text-white">Existing Users</h3>
            <button onClick={load} className="inline-flex items-center gap-2 text-xs font-bold text-gray-500 hover:text-purple-650 transition-colors">
              <RefreshCw size={13} /> Refresh
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {loading ? (
              <div className="col-span-full text-sm text-gray-400 text-center py-4">Loading users...</div>
            ) : users.length === 0 ? (
              <div className="col-span-full rounded-2xl border border-dashed border-gray-200 dark:border-slate-700 p-6 text-sm text-gray-405 text-center">
                No salon users added yet.
              </div>
            ) : users.map((user) => (
              <div key={user.id} className="rounded-2xl border border-gray-150 dark:border-slate-700 bg-gray-50/75 dark:bg-slate-900/40 p-5 flex flex-col justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-black text-gray-950 dark:text-white">{user.full_name}</p>
                    <span className="px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wider bg-purple-100 text-purple-750 dark:bg-purple-900/30 dark:text-purple-305">
                      {SALON_ROLE_DEFINITIONS[user.role]?.label || user.role}
                    </span>
                    {user.is_owner ? (
                      <span className="px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wider bg-amber-100 text-amber-705 dark:bg-amber-900/30 dark:text-amber-305">Owner</span>
                    ) : null}
                    {!user.is_active ? (
                      <span className="px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wider bg-rose-100 text-rose-705 dark:bg-rose-900/30 dark:text-rose-305">Inactive</span>
                    ) : null}
                  </div>
                  <p className="mt-1.5 text-xs text-gray-500 font-semibold">{user.email}</p>
                  {user.staff_name ? (
                    <p className="mt-1.5 text-xs text-gray-450">Linked staff: <strong className="text-gray-600 dark:text-gray-300">{user.staff_name}</strong></p>
                  ) : null}
                </div>
                {canManage ? (
                  <div className="flex items-center gap-2 border-t border-gray-100 dark:border-slate-800/80 pt-3 mt-1">
                    <button onClick={() => startEdit(user)} className="px-3.5 py-2 rounded-xl bg-white dark:bg-slate-800 border border-gray-250 dark:border-slate-750 text-xs font-bold text-gray-650 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-700 transition-all">
                      Edit
                    </button>
                    {!user.is_owner && user.is_active ? (
                      <button onClick={() => handleDeactivate(user.id)} className="px-3.5 py-2 rounded-xl bg-rose-50 hover:bg-rose-100 dark:bg-rose-950/20 text-xs font-bold text-rose-600 dark:text-rose-455 transition-all ml-auto">
                        Deactivate
                      </button>
                    ) : null}
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
